import { Capacitor } from "@capacitor/core";

export type SellerNativePlatform = "android" | "ios";
export type SellerNativeUpdateChannel = "store" | "testing";

export function getSellerNativePlatform(): SellerNativePlatform | null {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  const platform = Capacitor.getPlatform();
  return platform === "android" || platform === "ios" ? platform : null;
}

/** iOS is a companion to existing accounts; software purchases stay outside its UI. */
export function isIosCompanionApp(): boolean {
  return getSellerNativePlatform() === "ios";
}

export function isSellerNativeApp(): boolean {
  return getSellerNativePlatform() !== null;
}

export function isSellerNativeAppOrigin(): boolean {
  const platform = getSellerNativePlatform();
  if (!platform) return false;
  try {
    const url = new URL(window.location.href);
    // WKWebView reserves HTTPS, so Capacitor uses its default local scheme on iOS.
    return url.protocol === (platform === "ios" ? "capacitor:" : "https:") &&
      url.hostname === "seller.trioty.com" && !url.port && !url.username && !url.password;
  } catch {
    return false;
  }
}

export function getSellerNativeUpdateChannel(): SellerNativeUpdateChannel {
  return import.meta.env.VITE_NATIVE_UPDATE_CHANNEL === "testing" ? "testing" : "store";
}

export function isSellerOtaTestingEnabled(): boolean {
  return isSellerNativeApp() && getSellerNativeUpdateChannel() === "testing";
}

/** Remove browser-only seller caches if a native install inherited any. */
export async function clearSellerWebRuntime(): Promise<void> {
  if (!isSellerNativeApp()) {
    return;
  }

  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch {
      // Native use must not be blocked by a browser API implementation detail.
    }
  }

  if ("caches" in window) {
    try {
      const cacheKeys = await caches.keys();
      await Promise.all(
        cacheKeys
          .filter((key) => key.startsWith("seller-static-"))
          .map((key) => caches.delete(key)),
      );
    } catch {
      // Best-effort cleanup; the bundled native assets remain authoritative.
    }
  }
}

export function markSellerNativeDocument(): void {
  const platform = getSellerNativePlatform();
  if (!platform) {
    return;
  }

  document.documentElement.dataset.nativePlatform = platform;
  void clearSellerWebRuntime();
}
